/* eslint-disable import/no-cycle */
import { useMutation } from '@apollo/client';
import Avatar from 'antd/lib/avatar/avatar';
import { INPUT_TYPE } from 'components/common/Input/types';
import { InputProps } from 'components/InputFieldsGenerator/types';
import { ERRORS } from 'constants/notify';
import { usStatesSelectOptions } from 'constants/usStates';
import useCurrentLoggedInUser from 'hooks/apollo/queries/useCurrentLoggedInUser';
import useInfiniteDataQuery from 'hooks/useInfiniteDataQuery';
import { get, isEmpty } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CREATE_LOCATION } from 'services/graphql/mutations/location';
import { VALIDATE_SHIPPING_ACCOUNT_NO } from 'services/graphql/mutations/shipping';
import { GET_USERS_IN_LOCATION_FORM } from 'services/graphql/queries/user.query';
import { GeneralFunction } from 'types/common';
import { ILocation, IUsersLocationsInput, LocationRole } from 'types/location';
import { CompanyRoleEnum, IUser } from 'types/user';
import { defaultNotification, fastGetDefaultAvatarUrl } from 'utils';
import * as Yup from 'yup';

import LocationFormBasic from './LocationFormBasic';
import LocationFormDocumentShippingReturn from './LocationFormDocumentShippingReturn';
import LocationFormUserAssign from './LocationFormUserAssign';
import { IAssignLocationRoleTwoColumnMapper } from './types';

export interface IUseLocationFormProps {
  location?: ILocation;
  callbackOnSuccess?: GeneralFunction;
}

export const useShippingAccountNoValidateMutation = () => {
  const [validateShippingAccount] = useMutation(VALIDATE_SHIPPING_ACCOUNT_NO);
  const validateShippingAccountNumber = async (accountNo: string) => {
    try {
      const response = await validateShippingAccount({
        variables: {
          accountNo,
        },
      });
      return response;
    } catch (err) {
      defaultNotification({ failure: 'Shipping Account Number is invalid! Please try another one.' });
      return {};
    }
  };
  return validateShippingAccountNumber;
};

export const useLocationFormProps = (props?: IUseLocationFormProps) => {
  const { location, callbackOnSuccess } = props || {};
  const { usersLocations, ...locationProps } = location || {};
  const [inputValues, setInputValues] = useState<Record<string, any>>({});
  const [currentFormStep, setCurrentFormStep] = useState(0);
  // this state is to save the list of selected users in case findUser query is changed
  // so that the column of selected users is kept intact
  // due to it checking from the users found from findUser query
  const [selectedUsersByInputValues, setSelectedUsersByInputValues] = useState<Record<string, any>>({});

  const validateShippingAccountNumber = useShippingAccountNoValidateMutation();

  const locationFormValues = useMemo(() => {
    if (!locationProps || isEmpty(locationProps)) return undefined;
    const tempLocationProps = locationProps as ILocation;
    const tempLocationAddress = tempLocationProps.address;
    return {
      name: tempLocationProps?.name,
      number: tempLocationProps?.number,
      address1: tempLocationAddress?.address1,
      address2: tempLocationAddress?.address2,
      city: tempLocationAddress?.city,
      state: tempLocationAddress?.state,
      zip: tempLocationAddress?.zip,
    };
  }, [location]);

  const { data: users, loading, firstLoading, fetchNextPageData, fetchByQuery } = useInfiniteDataQuery<IUser>({
    graphQLQuery: GET_USERS_IN_LOCATION_FORM,
    queryProps: {
      variables: {
        companyRole: CompanyRoleEnum.REGULAR,
      },
    },
  });

  const isLocationAvailable = !!location?.id;

  useEffect(() => {
    const usersByInputValuesTemp =
      Object.keys(inputValues).reduce((acc, inputKey) => {
        const userWithInputKeyAsId = users.find((u) => u.id === inputKey);
        if (userWithInputKeyAsId) {
          acc[userWithInputKeyAsId.id] = userWithInputKeyAsId;
        }
        return acc;
      }, {} as Record<string, IUser>) || {};
    setSelectedUsersByInputValues((c) => ({ ...usersByInputValuesTemp, ...c }));
  }, [inputValues, users]);

  const selectedUsers = Object.values(selectedUsersByInputValues);

  useEffect(() => {
    if (location?.usersLocations) {
      const userLocationsInputValues =
        location.usersLocations.reduce((acc, userLocation) => {
          const uId = userLocation.user?.id;
          if (!uId) return acc;
          if (userLocation.manageUsersPermission) acc[uId] = LocationRole.LOCATION_ADMIN;
          else if (userLocation.clientPermission) {
            acc[uId] = LocationRole.EMPLOYEE;
          } else acc[uId] = LocationRole.NONE;
          return acc;
        }, {} as Record<string, any>) || {};
      setInputValues((c) => ({ ...c, ...userLocationsInputValues }));
    }
  }, [location]);

  useEffect(() => {
    if (users) {
      const usersWithLocationRoleObject =
        users.reduce((acc, u) => {
          const uId = u.id;
          if (inputValues[uId]) return acc;
          acc[u.id] = undefined;
          return acc;
        }, {} as Record<string, any>) || {};
      setInputValues((c) => ({ ...usersWithLocationRoleObject, ...c }));
    }
  }, [users]);

  const [createLocation, { loading: submitLoading }] = useMutation(CREATE_LOCATION);

  const locationGeneralInformationFormProps: InputProps[] = useMemo(
    () => [
      {
        name: 'number',
        label: 'Phone Number',
        placeholder: 'Phone number...',
        inputSettings: {
          isPhoneNumber: true,
        },
        sideFields: [
          {
            name: 'name',
            label: 'Location Name',
            size: 12,
          },
        ],
      },
      {
        name: 'address1',
        label: 'Address Line 1',
        addressSuggestion: true,
        addressSuggestionSubFields: {
          address1: 'address1',
          city: 'city',
          zip: 'zip',
          state: 'state',
        },
      },
      { name: 'address2', label: 'Address Line 2' },
      {
        name: 'city',
        label: 'City',
      },
      {
        name: 'zip',
        label: 'Zip',
        sideFields: [
          {
            name: 'state',
            label: 'State',
            type: INPUT_TYPE.SELECT,
            inputOptions: usStatesSelectOptions,
            size: 12,
          },
        ],
      },
    ],
    [],
  );
  const [validateShippingLoading, setValidateShippingLoading] = useState(false);
  const locationDocumentShippingReturnFormProps: InputProps[] = useMemo(
    () => [
      {
        name: 'fedExAccountNumber',
        label: (
          <span>
            FedEx Account Number <span className='text-red-500'>{validateShippingLoading ? '(Validating)' : ''}</span>
          </span>
        ),
      },
    ],
    [validateShippingLoading],
  );

  const initialLocationFormGeneralInformation = useMemo(
    () =>
      isLocationAvailable
        ? locationFormValues || {}
        : {
            name: '',
            address1: '',
            address2: '',
            city: '',
            state: '',
            zip: '',
            number: '',
          },
    [location],
  );
  const locationReturnShippingInitialValues = useMemo(
    () => ({
      fedExAccountNumber: location?.returnShippingAccount?.accountNo || '',
    }),
    [location],
  );

  const initialLocationFormUsersLocation = useMemo(
    () =>
      users.reduce((acc, u) => {
        const userInUsersLocation = usersLocations?.find((userLocation) => userLocation.user.id === u.id);
        let userPermission = LocationRole.NONE;
        if (userInUsersLocation?.manageUsersPermission) {
          userPermission = LocationRole.LOCATION_ADMIN;
        } else if (userInUsersLocation?.clientPermission) {
          userPermission = LocationRole.EMPLOYEE;
        }
        acc[u.id] = userPermission;
        return acc;
      }, {} as Record<string, LocationRole>),
    [location],
  );

  const onUserAssignFormLocationSearch = (v: string) => {
    fetchByQuery(v);
  };

  const initialValues = useMemo(
    () => ({ ...initialLocationFormGeneralInformation, ...initialLocationFormUsersLocation }),
    [initialLocationFormGeneralInformation, initialLocationFormUsersLocation],
  );

  const formComponents = useMemo(
    () => [
      { component: <LocationFormBasic inputProps={locationGeneralInformationFormProps} /> },
      {
        component: <LocationFormDocumentShippingReturn inputProps={locationDocumentShippingReturnFormProps} />,
      },
    ],
    [users, selectedUsers, inputValues, loading, locationGeneralInformationFormProps],
  );

  const formComponentForUserAssign = useMemo(
    () => ({
      component: (
        <LocationFormUserAssign
          fetchMoreUser={fetchNextPageData}
          selectedUsers={selectedUsers}
          onRightColumnSearch={onUserAssignFormLocationSearch}
          users={users}
          setInputValues={setInputValues}
          inputValues={inputValues}
          userFetchLoading={firstLoading}
        />
      ),
    }),
    [users, selectedUsers, inputValues, firstLoading],
  );

  const formComponentsBySteps = useMemo(
    () => (users?.length ? [...formComponents, formComponentForUserAssign] : formComponents),
    [formComponentForUserAssign, formComponents, !!users?.length],
  );

  const schema = useMemo(
    () => ({
      name: Yup.string().required('Required'),
      address1: Yup.string().required('Required'),
      number: Yup.string().validatePhoneNumber('Wrong format'),
      city: Yup.string().required('Required'),
      state: Yup.string().required('Required'),
      zip: Yup.string()
        .matches(/(^\d{5}$)|(^\d{9}$)|(^\d{5}-\d{4}$)/, 'Wrong format')
        .required('Required'),
      fedExAccountNumber: Yup.string()
        .matches(/^[0-9]*$/, 'Numbers only')
        .notRequired(),
    }),
    [],
  );

  const onNext = useCallback(
    async (v: Record<string, any>) => {
      if (validateShippingLoading) return;
      if (v.fedExAccountNumber && currentFormStep === 1) {
        setValidateShippingLoading(true);
        validateShippingAccountNumber(v.fedExAccountNumber).then((res) => {
          setValidateShippingLoading(false);
          if (!get(res, 'error')) {
            setCurrentFormStep((c) => {
              if (c < formComponentsBySteps.length - 1) return c + 1;
              return c;
            });
            setInputValues((c) => ({ ...c, ...v }));
          }
        });
      } else {
        setCurrentFormStep((c) => {
          if (c < formComponentsBySteps.length - 1) return c + 1;
          return c;
        });
      }
    },
    [currentFormStep, validateShippingLoading],
  );

  const onPrevious = useCallback(() => {
    setCurrentFormStep((c) => (c > 0 ? c - 1 : c));
  }, [currentFormStep]);

  const callCreateLocation = async (
    newLocationValues: Record<string, any>,
    callbackOnSuccessMutation?: GeneralFunction,
  ) => {
    const {
      address1,
      address2,
      city,
      name,
      state,
      zip,
      number,
      fedExAccountNumber,
      ...userLocationsValues
    } = newLocationValues;
    try {
      await createLocation({
        variables: {
          doc: {
            name,
            address: {
              address1,
              address2,
              city,
              state,
              zip,
            },
            returnShippingAccount: {
              accountNo: fedExAccountNumber,
            },
            number,
            usersLocations: Object.entries(userLocationsValues).reduce((acc, [userId, locationRoleValue]) => {
              if (locationRoleValue === undefined) return acc;
              acc.push({
                clientPermission: locationRoleValue !== LocationRole.NONE,
                manageLocationsPermission: false,
                manageUsersPermission: locationRoleValue === LocationRole.LOCATION_ADMIN,
                user: userId,
              });
              return acc;
            }, [] as IUsersLocationsInput[]),
          },
        },
      });
      defaultNotification({ success: 'Create Location Successfully!' });
      callbackOnSuccessMutation?.();
    } catch (err) {
      console.log('err', err);
      defaultNotification({ failure: ERRORS.DEFAULT_MUTATION_ERROR });
    }
  };

  const onSubmit = async (values: Record<string, any>, resetForm: GeneralFunction) => {
    validateShippingAccountNumber(values.fedExAccountNumber).then((response) => {
      if (!get(response, 'error'))
        callCreateLocation(values, () => {
          callbackOnSuccess?.();
          resetForm?.();
          setCurrentFormStep(0);
          setInputValues({});
          setSelectedUsersByInputValues({});
        });
    });
  };

  return {
    currentFormStep,
    onNext,
    onPrevious,
    onSubmit,
    initialValues,
    hasNext: currentFormStep < formComponentsBySteps.length - 1,
    hasPrevious: currentFormStep > 0,
    loading,
    firstLoading,
    schema: Yup.object(schema),
    formComponentsBySteps,
    users,
    locationGeneralInformationFormProps,
    locationGeneralInformationInitialValues: initialLocationFormGeneralInformation,
    locationReturnShippingInitialValues,
    locationDocumentShippingReturnFormProps,
    onUserAssignFormLocationSearch,
    selectedUsers,
    fetchNextPageData,
    submitLoading,
  };
};

export const useTwoColumnsLocationFormProps: () => IAssignLocationRoleTwoColumnMapper = () => {
  const { companyRole, usersLocations } = useCurrentLoggedInUser(); // test nha
  const isCurrentUserCompanyAdmin = companyRole === CompanyRoleEnum.ADMIN;
  const usersLocationsWithPermissions = usersLocations?.reduce((acc, perm) => {
    if (perm.manageUsersPermission) acc.push(perm.location.id);
    return acc;
  }, [] as string[]);

  const rightColumnMapper = (u: IUser) => ({
    component: (
      <div className='flex items-center'>
        <div className='w-8'>
          <Avatar src={fastGetDefaultAvatarUrl(u)} />
        </div>
        <div className='px-2 line-clamp-2'>
          {u.firstName} {u.lastName}
        </div>
      </div>
    ),
    className: !isCurrentUserCompanyAdmin && !usersLocationsWithPermissions?.includes(u.id) ? 'greyed-out' : '',
    disableRow: !isCurrentUserCompanyAdmin && !usersLocationsWithPermissions?.includes(u.id),
  });

  const leftColumnMapper = (u: IUser) => ({
    component: (
      <div className='flex items-center'>
        <div>
          <Avatar src={fastGetDefaultAvatarUrl(u)} />
        </div>
        <div className='px-2 line-clamp-2'>
          {u.firstName} {u.lastName}
        </div>
      </div>
    ),
    className: !isCurrentUserCompanyAdmin && !usersLocationsWithPermissions?.includes(u.id) ? 'greyed-out' : '',
    disableRow: !isCurrentUserCompanyAdmin && !usersLocationsWithPermissions?.includes(u.id),
  });

  return {
    rightColumnMapper,
    leftColumnMapper,
  };
};
